import { resolveLocale, type SupportedLocale } from '@fleetcore/shared'

/**
 * Every VITE_* variable is read exactly once, here, and never again from a
 * component. Vite inlines these at build time, so a typo in a component would
 * silently become `undefined` at runtime instead of failing the build — reading
 * them in one place makes the fallbacks explicit and greppable.
 *
 * The names match .env.example verbatim.
 */
function readUrl(raw: string | undefined, fallback: string): string {
  const value = (raw ?? '').trim()
  if (value.length === 0) return fallback
  // A trailing slash here turns every request path into a double slash, which
  // some reverse proxies answer with a 301 the browser will not repeat with the
  // Authorization header attached.
  return value.replace(/\/+$/, '')
}

/**
 * Esri World Imagery. Free for light use with the attribution below, which is why it
 * is the default rather than the answer — a production tenant points this at its own
 * Mapbox/Google/IGN key.
 */
const DEFAULT_SATELLITE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

const DEFAULT_SATELLITE_ATTRIBUTION =
  'Esri, Maxar, Earthstar Geographics, and the GIS User Community'

/**
 * Street names, town names and places (cafés, pharmacies, fuel, schools…) drawn ON
 * the satellite imagery, so the aerial view reads like a hybrid map rather than a bare
 * photo. OpenFreeMap: OpenStreetMap vector tiles, free, no key. An empty
 * `VITE_MAP_LABELS_URL` turns the overlay off.
 */
const DEFAULT_LABELS_URL = 'https://tiles.openfreemap.org/planet'
const DEFAULT_LABELS_SPRITE_URL = 'https://tiles.openfreemap.org/sprites/ofm_f384/ofm'
const DEFAULT_LABELS_ATTRIBUTION = '© OpenFreeMap © OpenMapTiles © OpenStreetMap contributors'

/**
 * Font PBFs, needed only for cluster counts and plate labels — the markers render
 * without them. Self-host this before an air-gapped install: it is the one external
 * request the map makes besides the tiles themselves.
 */
const DEFAULT_GLYPHS_URL = 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf'

export interface WebEnv {
  apiUrl: string
  wsUrl: string
  mapTileUrl: string
  mapAttribution: string
  /** Optional dark counterpart. Empty means "tint the light basemap instead". */
  mapTileUrlDark: string
  mapSatelliteUrl: string
  mapSatelliteAttribution: string
  mapGlyphsUrl: string
  /** TileJSON of an OpenMapTiles-schema vector source; empty disables the overlay. */
  mapLabelsUrl: string
  mapLabelsSpriteUrl: string
  mapLabelsAttribution: string
  /**
   * Live traffic, as an XYZ raster template. Empty means "no provider": the overlay
   * stays locked with the reason, rather than offering a toggle that draws nothing.
   */
  mapTrafficTileUrl: string
  mapTrafficAttribution: string
  defaultLocale: SupportedLocale
  isProduction: boolean
}

export const env: WebEnv = {
  apiUrl: readUrl(import.meta.env.VITE_API_URL, 'http://localhost:3000'),
  wsUrl: readUrl(import.meta.env.VITE_WS_URL, 'ws://localhost:3000'),
  mapTileUrl:
    import.meta.env.VITE_MAP_TILE_URL?.trim() || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  mapAttribution: import.meta.env.VITE_MAP_ATTRIBUTION?.trim() || '© OpenStreetMap contributors',
  mapTileUrlDark: import.meta.env.VITE_MAP_TILE_URL_DARK?.trim() || '',
  mapSatelliteUrl: import.meta.env.VITE_MAP_SATELLITE_URL?.trim() || DEFAULT_SATELLITE_URL,
  mapSatelliteAttribution:
    import.meta.env.VITE_MAP_SATELLITE_ATTRIBUTION?.trim() || DEFAULT_SATELLITE_ATTRIBUTION,
  mapGlyphsUrl: import.meta.env.VITE_MAP_GLYPHS_URL?.trim() || DEFAULT_GLYPHS_URL,
  // `??` not `||`: an explicitly empty value is how a deployment switches it off.
  mapLabelsUrl: (import.meta.env.VITE_MAP_LABELS_URL ?? DEFAULT_LABELS_URL).trim(),
  mapLabelsSpriteUrl:
    import.meta.env.VITE_MAP_LABELS_SPRITE_URL?.trim() || DEFAULT_LABELS_SPRITE_URL,
  mapLabelsAttribution:
    import.meta.env.VITE_MAP_LABELS_ATTRIBUTION?.trim() || DEFAULT_LABELS_ATTRIBUTION,
  mapTrafficTileUrl: import.meta.env.VITE_MAP_TRAFFIC_TILE_URL?.trim() || '',
  mapTrafficAttribution: import.meta.env.VITE_MAP_TRAFFIC_ATTRIBUTION?.trim() || '',
  defaultLocale: resolveLocale(import.meta.env.VITE_DEFAULT_LOCALE),
  isProduction: import.meta.env.PROD,
}
